/* eslint-disable */
import { useState, useEffect, useCallback, useRef } from 'react';

// Debounce hook
export const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// Pagination hook
export const usePagination = (initialLimit = 25) => {
  const [page,  setPage]  = useState(1);
  const [limit, setLimit] = useState(initialLimit);
  const reset = () => setPage(1);
  return { page, limit, setPage, setLimit, reset };
};

// Data fetching hook
export const useFetch = (fetchFn, deps = [], immediate = true) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [error,   setError]   = useState(null);
  const mounted = useRef(true);

  const fetch = useCallback(async (...args) => {
    setLoading(true); setError(null);
    try {
      const res = await fetchFn(...args);
      if (mounted.current) setData(res.data ?? res);
    } catch (err) {
      if (mounted.current) setError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (immediate) fetch();
    return () => { mounted.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, error, refetch: fetch };
};

// Modal hook
export const useModal = () => {
  const [open,    setOpen]    = useState(false);
  const [payload, setPayload] = useState(null);
  const show  = (p = null) => { setPayload(p); setOpen(true); };
  const close = ()          => { setOpen(false); setPayload(null); };
  return { open, payload, show, close };
};

// Number formatter
export const fmt = {
  currency: (n, curr = 'INR') => {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: curr, maximumFractionDigits: 0 }).format(n);
  },
  number: (n) => {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('en-IN').format(n);
  },
  // timeZone pinned to IST explicitly — without it, toLocaleString falls
  // back to whatever timezone the browser/OS is set to, which drifted from
  // IST on at least one viewing device despite 'en-IN' controlling the
  // date's display *format* (DD Mon YYYY) the whole time, not its zone.
  date: (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  },
  datetime: (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
  },
  truncate: (s, n = 40) => s && s.length > n ? s.slice(0, n) + '…' : (s || '—'),
};

// Status badge config
export const STATUS_CONFIG = {
  active:           { label: 'Active',          cls: 'badge-green'  },
  transferred:      { label: 'Transferred',      cls: 'badge-blue'   },
  scrapped:         { label: 'Scrapped',         cls: 'badge-red'    },
  disposed:         { label: 'Disposed',         cls: 'badge-red'    },
  sold:             { label: 'Sold',             cls: 'badge-purple' },
  under_maintenance:{ label: 'Under Maintenance',cls: 'badge-amber'  },
  checked_out:      { label: 'Checked Out',      cls: 'badge-teal'   },
  pending:          { label: 'Pending',          cls: 'badge-amber'  },
  approved:         { label: 'Approved',         cls: 'badge-green'  },
  rejected:         { label: 'Rejected',         cls: 'badge-red'    },
  completed:        { label: 'Completed',        cls: 'badge-green'  },
  returned:         { label: 'Returned',         cls: 'badge-green'  },
  overdue:          { label: 'Overdue',          cls: 'badge-red'    },
  rfid:             { label: 'RFID',             cls: 'badge-purple' },
  qr:               { label: 'QR Code',          cls: 'badge-blue'   },
  barcode:          { label: 'Barcode',          cls: 'badge-teal'   },
  multiple:         { label: 'Multiple',         cls: 'badge-gray'   },
};

export const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'badge-gray' };
  return <span className={`badge ${cfg.cls}`}>{cfg.label}</span>;
};
